// Line viz — interactive multi-series time chart. The one report viz that shows
// a value "over time": each distinct value of the series column (e.g.
// `nodename`) becomes its own line, plotting Y against X (typically a TIMESTAMP
// `time` column, in µs). Built for the collector `metrics` table — chart
// reads/sec, writes/sec, CPU%, or memory per node over time.
//
// Column resolution:
//   - panel.lineXCol      → X axis (default: a column literally named "time",
//                           else the first numeric column)
//   - panel.lineYCol      → Y axis (default: a column named "value", else the
//                           first numeric column that isn't X)
//   - panel.lineSeriesCol → series/group (default: first non-numeric column)
//   - panel.lineAgg       → initial aggregate line: none|sum|avg|min|max
//                           (default 'none'); the user can change it live via
//                           the dropdown.
//
// Rows are grouped by series, each sorted ascending by X, positioned on a SHARED
// x-scale so series sampled at slightly different instants line up in time.
// Caps display at panel.lineMaxSeries (default 8). The aggregate line and the
// hover read-out both align unsynchronized series with last-observation-carried-
// forward (LOCF): at any X, each series contributes its most recent value.

import { useRef, useState } from 'react';

const PALETTE = [
  '#00E5C7', '#0099FF', '#FFB454', '#A78BFA',
  '#F472B6', '#FBBF24', '#34D399', '#60A5FA', '#9CA3AF',
];
// Aggregate line: a bold rose-red NOT in the node palette, so the total reads
// unmistakably as "the line that isn't a node". Drawn solid + thick with a soft
// glow underlay so it pops above the per-node lines under load.
const AGG_COLOR = '#FF4D6D';
const AGG_OPTIONS = ['none', 'sum', 'avg', 'min', 'max'];

function isNumericCell(v) {
  if (typeof v === 'number') return Number.isFinite(v);
  if (typeof v === 'string') return /^-?\d+(\.\d+)?$/.test(v);
  return false;
}
function asNumber(v) {
  if (typeof v === 'number') return v;
  if (typeof v === 'string' && /^-?\d+(\.\d+)?$/.test(v)) return parseFloat(v);
  return 0;
}

// Format a Y value compactly (bytes-ish gets GB/MB; big counts get k/M).
function fmtY(n) {
  if (!Number.isFinite(n)) return String(n);
  if (Math.abs(n) >= 1024 ** 3) return (n / 1024 ** 3).toFixed(1) + 'G';
  if (Math.abs(n) >= 1024 ** 2) return (n / 1024 ** 2).toFixed(1) + 'M';
  if (Math.abs(n) >= 1000)      return (n / 1000).toFixed(1) + 'k';
  return String(Math.round(n));
}

// Format an x tick. `time` is µs since epoch → local clock time; else raw.
function fmtX(v, isTime) {
  if (!isTime) return String(Math.round(v));
  const d = new Date(v / 1000);
  if (Number.isNaN(d.getTime())) return String(v);
  return d.toLocaleTimeString();
}

function aggregate(kind, vals) {
  if (!vals.length) return null;
  switch (kind) {
    case 'sum': return vals.reduce((a, b) => a + b, 0);
    case 'avg': return vals.reduce((a, b) => a + b, 0) / vals.length;
    case 'min': return Math.min(...vals);
    case 'max': return Math.max(...vals);
    default:    return null;
  }
}

export function LineViz({ columns, rows, error, loading, panel, onZoom, onZoomOut }) {
  // Hooks must run before any early return.
  const [agg, setAgg] = useState(
    AGG_OPTIONS.includes(panel?.lineAgg) ? panel.lineAgg : 'none',
  );
  const [hover, setHover] = useState(null); // index into the aligned timeline
  // Drag-to-zoom selection, in viewBox x coords: {x0, x1} while dragging.
  // The ref is the source of truth for the pointer handlers (a fast drag can
  // deliver down/move/up before React re-renders, leaving handler closures
  // over stale state); the state mirror only drives the rendered band.
  const selRef = useRef(null);
  const [sel, setSel] = useState(null);

  if (error) return <div className="report-viz-empty error">{error}</div>;
  if (loading && !rows) return <div className="report-viz-empty">Loading…</div>;
  if (!rows?.length) return <div className="report-viz-empty">No rows</div>;
  if (!columns?.length) return <div className="report-viz-empty">No columns</div>;

  const idxOf = (name) => (name ? columns.indexOf(name) : -1);
  const numericInAny = (i) => rows.some(r => isNumericCell(r[i]));

  let xIdx = idxOf(panel?.lineXCol);
  if (xIdx < 0) xIdx = columns.indexOf('time');
  if (xIdx < 0) xIdx = columns.findIndex((_, i) => numericInAny(i));

  let yIdx = idxOf(panel?.lineYCol);
  if (yIdx < 0) yIdx = columns.indexOf('value');
  if (yIdx < 0) yIdx = columns.findIndex((_, i) => i !== xIdx && numericInAny(i));

  let sIdx = idxOf(panel?.lineSeriesCol);
  if (sIdx < 0) sIdx = columns.findIndex((_, i) => i !== xIdx && i !== yIdx && !numericInAny(i));
  if (sIdx < 0) sIdx = columns.findIndex((_, i) => i !== xIdx && i !== yIdx);

  if (xIdx < 0 || yIdx < 0) {
    return <div className="report-viz-empty">Line viz needs an X and a numeric Y column.</div>;
  }
  const isTime = columns[xIdx] === 'time';

  // Group rows by series, collect (x, y), sort each ascending by x.
  const groups = new Map();
  for (const r of rows) {
    const key = sIdx >= 0 ? String(r[sIdx] ?? '') : (columns[yIdx] || 'value');
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push({ x: asNumber(r[xIdx]), y: asNumber(r[yIdx]) });
  }
  // Full set drives the aggregate + hover read-out; `series` is the displayed
  // (capped) subset. slice() preserves order so series[i] === allSeries[i].
  const allSeries = [...groups.entries()]
    .map(([name, pts]) => ({ name, pts: pts.sort((a, b) => a.x - b.x) }));
  const maxSeries = Number.isFinite(panel?.lineMaxSeries) ? panel.lineMaxSeries : 8;
  const series = allSeries.slice(0, maxSeries);

  // Aligned timeline (union of all x) + LOCF value of every series at each x.
  const allX = [...new Set(allSeries.flatMap(s => s.pts.map(p => p.x)))].sort((a, b) => a - b);
  const ptr = allSeries.map(() => 0);
  const cur = allSeries.map(() => null);
  const frames = allX.map((x) => {
    allSeries.forEach((s, i) => {
      while (ptr[i] < s.pts.length && s.pts[ptr[i]].x <= x) { cur[i] = s.pts[ptr[i]].y; ptr[i]++; }
    });
    return { x, vals: cur.slice() };
  });

  // Aggregate line (LOCF over all series present at each frame).
  const aggPts = agg === 'none' ? [] : frames
    .map(f => {
      const present = f.vals.filter(v => v != null);
      const y = aggregate(agg, present);
      return y == null ? null : { x: f.x, y };
    })
    .filter(Boolean);

  // Shared scales (include 0 and the aggregate so nothing clips).
  let xMin = Infinity, xMax = -Infinity, yMax = 0;
  for (const s of series) for (const p of s.pts) {
    if (p.x < xMin) xMin = p.x;
    if (p.x > xMax) xMax = p.x;
    if (p.y > yMax) yMax = p.y;
  }
  for (const p of aggPts) { if (p.y > yMax) yMax = p.y; }
  if (!Number.isFinite(xMin)) return <div className="report-viz-empty">No plottable points</div>;
  const xRange = (xMax - xMin) || 1;
  const yRange = yMax || 1;

  const W = 1000, H = 260, PAD_L = 56, PAD_R = 12, PAD_T = 12, PAD_B = 28;
  const innerW = W - PAD_L - PAD_R, innerH = H - PAD_T - PAD_B;
  const xAt = (x) => PAD_L + innerW * (x - xMin) / xRange;
  const yAt = (y) => PAD_T + innerH * (1 - y / yRange);

  const yTicks = [0, yMax / 2, yMax];
  const xTicks = [xMin, xMin + xRange / 2, xMax];

  // Map a pointer event to viewBox x (or null when outside the plot area).
  const localX = (e) => {
    const svg = e.currentTarget;
    const ctm = svg.getScreenCTM();
    if (!ctm) return null;
    const pt = svg.createSVGPoint();
    pt.x = e.clientX; pt.y = e.clientY;
    return pt.matrixTransform(ctm.inverse()).x;
  };
  const clampX = (x) => Math.max(PAD_L, Math.min(W - PAD_R, x));
  const dataXAt = (px) => xMin + (clampX(px) - PAD_L) / innerW * xRange;

  const setHoverAt = (px) => {
    if (px == null || px < PAD_L || px > W - PAD_R || !frames.length) { setHover(null); return; }
    const dataX = dataXAt(px);
    let nearest = 0, best = Infinity;
    for (let i = 0; i < allX.length; i++) {
      const d = Math.abs(allX[i] - dataX);
      if (d < best) { best = d; nearest = i; }
    }
    setHover(nearest);
  };

  // Drag-to-zoom (Grafana-style): only meaningful when x is a time axis and
  // the report gave us a zoom callback. Pointer events cover mouse AND
  // touch/pencil; MIN_SEL_PX distinguishes a drag from a sloppy tap.
  const zoomable = isTime && typeof onZoom === 'function';
  const MIN_SEL_PX = 10; // viewBox units
  const onPointerDown = (e) => {
    if (!zoomable || (e.pointerType === 'mouse' && e.button !== 0)) return;
    const x = localX(e);
    if (x == null || x < PAD_L || x > W - PAD_R) return;
    e.preventDefault(); // suppress text selection / native drag during the sweep
    e.currentTarget.setPointerCapture?.(e.pointerId);
    selRef.current = { x0: x, x1: x };
    setSel(selRef.current);
  };
  const onPointerMove = (e) => {
    const x = localX(e);
    setHoverAt(x);
    if (selRef.current && x != null) {
      selRef.current = { ...selRef.current, x1: clampX(x) };
      setSel(selRef.current);
    }
  };
  const onPointerUp = () => {
    const s = selRef.current;
    selRef.current = null;
    setSel(null);
    if (!s || Math.abs(s.x1 - s.x0) < MIN_SEL_PX) return; // a tap, not a selection
    onZoom(Math.round(dataXAt(s.x0)), Math.round(dataXAt(s.x1)));
  };
  const cancelSel = () => { selRef.current = null; setSel(null); setHover(null); };

  const hf = hover != null ? frames[hover] : null;
  const hoverAgg = hf && agg !== 'none' ? aggregate(agg, hf.vals.filter(v => v != null)) : null;
  // Latest aggregate value (last point of the aggregate line) — shown in the
  // legend when NOT hovering, mirroring how each series shows its latest value.
  const latestAgg = aggPts.length ? aggPts[aggPts.length - 1].y : null;
  const shownAgg = hoverAgg != null ? hoverAgg : latestAgg;

  return (
    <div className="report-viz-line" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {/* aggregate dropdown + hovered time */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 12 }}>
        <label style={{ color: 'var(--app-fg3)', textTransform: 'uppercase', letterSpacing: '0.08em', fontSize: 11 }}>aggregate</label>
        <select value={agg} onChange={e => setAgg(e.target.value)}
          style={{ background: 'var(--app-surface)', border: '1px solid var(--app-border)', borderRadius: 6, padding: '3px 8px', fontSize: 12, color: 'var(--app-fg1)', cursor: 'pointer' }}>
          {AGG_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
        {hf && (
          <span style={{ marginLeft: 'auto', color: 'var(--app-fg3)', fontFamily: 'var(--app-mono)' }}>{fmtX(hf.x, isTime)}</span>
        )}
      </div>

      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none"
        style={{
          width: '100%', height: 240, display: 'block',
          // pan-y: vertical swipes still scroll the page on touch devices;
          // horizontal drags reach us as pointer events for the zoom select.
          touchAction: zoomable ? 'pan-y' : 'auto',
          cursor: zoomable ? 'crosshair' : 'default',
          userSelect: 'none', WebkitUserSelect: 'none',
        }}
        onPointerDown={onPointerDown} onPointerMove={onPointerMove}
        onPointerUp={onPointerUp} onPointerCancel={cancelSel} onPointerLeave={cancelSel}
        onDoubleClick={() => onZoomOut?.()}>
        {zoomable && <title>Drag to zoom into a time range; double-click to zoom back out</title>}
        {/* gridlines + y labels */}
        {yTicks.map((t, i) => (
          <g key={i}>
            <line x1={PAD_L} x2={W - PAD_R} y1={yAt(t)} y2={yAt(t)} stroke="var(--app-border)" strokeWidth="1" />
            <text x={PAD_L - 8} y={yAt(t)} dy="4" textAnchor="end" fill="var(--app-fg3)" fontSize="11" fontFamily="var(--app-mono)">{fmtY(t)}</text>
          </g>
        ))}
        {/* x labels */}
        {xTicks.map((t, i) => (
          <text key={i} x={xAt(t)} y={H - 8} textAnchor={i === 0 ? 'start' : i === xTicks.length - 1 ? 'end' : 'middle'}
            fill="var(--app-fg3)" fontSize="11" fontFamily="var(--app-mono)">{fmtX(t, isTime)}</text>
        ))}
        {/* one polyline (or dot, for a single point) per series */}
        {series.map((s, i) => {
          const color = PALETTE[i % PALETTE.length];
          if (s.pts.length === 1) {
            const p = s.pts[0];
            return <circle key={i} cx={xAt(p.x)} cy={yAt(p.y)} r="2.5" fill={color} />;
          }
          return (
            <polyline key={i} fill="none" stroke={color} strokeWidth="1.5"
              points={s.pts.map(p => `${xAt(p.x)},${yAt(p.y)}`).join(' ')} />
          );
        })}
        {/* aggregate line — bold rose-red with a soft glow underlay so the
            total stands out above the per-node lines */}
        {aggPts.length > 1 && (() => {
          const pts = aggPts.map(p => `${xAt(p.x)},${yAt(p.y)}`).join(' ');
          return (
            <g>
              <polyline fill="none" stroke={AGG_COLOR} strokeWidth="7" strokeOpacity="0.18"
                strokeLinejoin="round" points={pts} />
              <polyline fill="none" stroke={AGG_COLOR} strokeWidth="2.75" strokeLinejoin="round" points={pts} />
            </g>
          );
        })()}
        {/* drag-to-zoom selection band */}
        {sel && Math.abs(sel.x1 - sel.x0) >= 2 && (
          <rect
            x={Math.min(sel.x0, sel.x1)} y={PAD_T}
            width={Math.abs(sel.x1 - sel.x0)} height={innerH}
            fill="var(--cyan)" fillOpacity="0.12"
            stroke="var(--cyan)" strokeOpacity="0.5" strokeWidth="1"
          />
        )}
        {/* hover crosshair + per-series dots */}
        {hf && (
          <g>
            <line x1={xAt(hf.x)} x2={xAt(hf.x)} y1={PAD_T} y2={H - PAD_B} stroke="var(--cyan)" strokeOpacity="0.4" strokeWidth="1" />
            {series.map((s, i) => hf.vals[i] != null && (
              <circle key={i} cx={xAt(hf.x)} cy={yAt(hf.vals[i])} r="3" fill={PALETTE[i % PALETTE.length]} stroke="var(--app-bg)" strokeWidth="1" />
            ))}
            {hoverAgg != null && (
              <circle cx={xAt(hf.x)} cy={yAt(hoverAgg)} r="4.5" fill={AGG_COLOR} stroke="var(--app-bg)" strokeWidth="1.5" />
            )}
          </g>
        )}
      </svg>

      {/* legend — shows each series' value at the hovered point (else its latest) */}
      <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexWrap: 'wrap', gap: 14, fontSize: 12, fontFamily: 'var(--app-mono)' }}>
        {series.map((s, i) => {
          const v = hf ? hf.vals[i] : (s.pts.length ? s.pts[s.pts.length - 1].y : null);
          return (
            <li key={i} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ width: 12, height: 2, background: PALETTE[i % PALETTE.length], borderRadius: 1 }} />
              <span style={{ color: 'var(--app-fg2)' }} title={s.name}>{s.name}</span>
              {v != null && <span style={{ color: 'var(--app-fg1)' }}>{fmtY(v)}</span>}
            </li>
          );
        })}
        {agg !== 'none' && (
          <li style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ width: 14, height: 3, background: AGG_COLOR, borderRadius: 1 }} />
            <span style={{ color: AGG_COLOR, fontWeight: 600 }}>{agg}</span>
            {shownAgg != null && <span style={{ color: 'var(--app-fg1)' }}>{fmtY(shownAgg)}</span>}
          </li>
        )}
        {groups.size > series.length && (
          <li style={{ color: 'var(--app-fg3)' }}>+{groups.size - series.length} more</li>
        )}
      </ul>
    </div>
  );
}
